// Single source of truth for palette colors. Both the mesher and the
// palette-panel look up colors here so they stay in sync.
//
// Index 0 is reserved as "empty" (never drawn). Entries 1..PALETTE_SIZE
// are the user-selectable colors. The user can recolor any swatch at runtime
// (see setPaletteColor).
//
// The live COLORS table belongs to the CURRENT document: it is swapped per
// model (applyPaletteSnapshot on load, snapshotPalette on save) so editing a
// swatch only affects the model you're editing — never the colors baked into
// other saved models. Voxels store palette INDICES, so without per-model
// snapshots a swatch edit would retroactively recolor that index everywhere.
// (Crucially there is NO global persistence here anymore — that was the source
// of cross-model color bleed.)
//
// Indices 1..PALETTE_SIZE are the user's swatches. Indices above that are
// "frozen" slots: when a swatch is recolored, the voxels already painted with it
// are re-indexed (see canvas-ops.recolorSwatch) into a frozen slot holding their
// OLD color, so editing a swatch never recolors existing voxels — only future
// painting does. The table therefore grows past PALETTE_SIZE; snapshot/restore
// cover the whole range so frozen colors round-trip with the model.

import * as THREE from "three";
import type { PaletteIndex } from "../model/voxel-grid";

// Factory-default colors. Editing a swatch overlays these; Reset restores them;
// a model with no saved palette (legacy file) also falls back to these.
const DEFAULT_COLORS: readonly number[] = [
  0x000000, // 0 — empty (reserved, never displayed)
  0xeaeaea, // 1  white
  0xc0c0c0, // 2  light gray
  0x808080, // 3  gray
  0x303030, // 4  near-black
  0xc7522a, // 5  terracotta
  0xd47a32, // 6  orange
  0xe2c275, // 7  mustard
  0xf2e36b, // 8  yellow
  0xb7c47a, // 9  olive
  0x6ec0a3, // 10 teal
  0x5a92d9, // 11 sky blue
  0x4a4ac0, // 12 indigo
  0x9b6dc4, // 13 violet
  0xc7659c, // 14 magenta
  0x9aa6c9, // 15 slate
  0x8a6a4b, // 16 brown
  0x6fae6a, // 17 green
  0xd98a9c, // 18 rose
];

// Live table — the current document's palette. Starts at the factory defaults;
// replaced per model via applyPaletteSnapshot. Mutated in place so existing
// lookups (paletteColor / paletteColorCss) keep seeing the current values.
const COLORS: number[] = [...DEFAULT_COLORS];

export const PALETTE_SIZE = DEFAULT_COLORS.length - 1;

// Resolve a single sRGB integer from a (possibly absent/partial) color table,
// falling back to the factory default for that index. Shared by the live
// lookups and the per-model lookups baked for layout placements.
function colorIntAt(colors: readonly number[] | undefined, p: PaletteIndex): number {
  const v = colors?.[p];
  return (typeof v === "number" && Number.isFinite(v) ? v : DEFAULT_COLORS[p] ?? 0x888888) &
    0xffffff;
}

// Returns a fresh Color so callers can mutate without aliasing the
// internal table. (Same convention the old PaletteLookup used.)
export function paletteColor(p: PaletteIndex): THREE.Color {
  return new THREE.Color(COLORS[p] ?? 0x888888);
}

// Allocation-free variant for per-frame callers: writes into `out`.
// setHex applies the same sRGB→linear conversion as the constructor.
export function paletteColorInto(p: PaletteIndex, out: THREE.Color): THREE.Color {
  return out.setHex(COLORS[p] ?? 0x888888);
}

// CSS color string for the same index — useful for the 2D canvas panel.
// Derive it straight from the stored sRGB integer (the same source as
// paletteColorHex) so the 2D swatch matches the color editor exactly.
// Don't route through paletteColor(): THREE color management converts the
// sRGB hex to *linear* on construction, so its r/g/b are linear-light and
// scaling them by 255 would render the swatch noticeably darker than the
// real color. (The mesher still uses paletteColor() — feeding the linear
// Color into a MeshStandardMaterial is correct for GPU rendering.)
export function paletteColorCss(p: PaletteIndex): string {
  const v = (COLORS[p] ?? 0x888888) & 0xffffff;
  return `#${v.toString(16).padStart(6, "0")}`;
}

// "#rrggbb" form for an <input type="color"> control.
export function paletteColorHex(p: PaletteIndex): string {
  const v = (COLORS[p] ?? 0x888888) & 0xffffff;
  return `#${v.toString(16).padStart(6, "0")}`;
}

// The factory default for a swatch index (used to detect / restore defaults).
export function factoryColor(p: PaletteIndex): number {
  return (DEFAULT_COLORS[p] ?? 0x888888) & 0xffffff;
}

// Raw sRGB int read/write at ANY index (swatch or frozen). The grid stores
// indices 1..255; callers that manage frozen slots (canvas-ops) use these to
// stash an old color while leaving the rest of the table alone.
export function colorInt(p: PaletteIndex): number {
  return (COLORS[p] ?? factoryColor(p)) & 0xffffff;
}
export function setColorInt(p: PaletteIndex, value: number): void {
  if (p < 1 || p > 255) return;
  COLORS[p] = value & 0xffffff;
}

// Parse a number or CSS/hex string into an sRGB int via THREE's color parser.
export function parseColorInt(color: number | string): number {
  return new THREE.Color(color).getHex();
}

// Recolor a swatch at runtime, in place. Low-level: does NOT preserve existing
// voxels — callers that need that go through canvas-ops.recolorSwatch. No-ops
// for the reserved index 0 or out-of-range.
export function setPaletteColor(p: PaletteIndex, color: number | string): void {
  if (p < 1 || p > PALETTE_SIZE) return;
  COLORS[p] = parseColorInt(color);
}

// Restore every swatch in the current document to its factory default (in place;
// does not preserve existing voxels — canvas-ops.resetSwatches does that).
export function resetPalette(): void {
  for (let i = 1; i <= PALETTE_SIZE; i++) COLORS[i] = DEFAULT_COLORS[i];
}

// Snapshot the WHOLE live palette (swatches + frozen slots) for saving with a
// model or for an undo step. Frozen colors must ride along so a re-indexed voxel
// keeps its color across save/load and undo/redo.
export function snapshotPalette(): number[] {
  return COLORS.slice();
}

// Replace the live document palette from a snapshot, rebuilding the entire table
// (so stale frozen slots from a previously-open model are dropped). Swatch
// indices missing from the snapshot fall back to the factory default; frozen
// indices missing from it are cleared (no voxel in a consistent model references
// them). Legacy models with no palette at all → all factory defaults.
export function applyPaletteSnapshot(colors: readonly number[] | undefined): void {
  const src = Array.isArray(colors) ? colors : undefined;
  const n = Math.max(PALETTE_SIZE + 1, src?.length ?? 0);
  COLORS.length = n;
  COLORS[0] = DEFAULT_COLORS[0];
  for (let i = 1; i < n; i++) {
    const v = src?.[i];
    if (typeof v === "number" && Number.isFinite(v)) COLORS[i] = v & 0xffffff;
    else COLORS[i] = i <= PALETTE_SIZE ? DEFAULT_COLORS[i] : 0; // swatch→default, frozen→clear
  }
}

// Build a standalone color lookup over a fixed snapshot (NOT the live table).
// Layout placements use this so each baked model keeps its own saved colors,
// independent of whatever document is currently open. Falls back per index to
// the factory default; a wholly-absent table (legacy model) yields defaults.
export function makePaletteLookup(
  colors: readonly number[] | undefined,
): { color(p: PaletteIndex): THREE.Color } {
  // Copy defensively; tolerate a malformed table from a corrupt/hand-edited file
  // (colorIntAt falls back to the factory default per missing/invalid index).
  const table = Array.isArray(colors) ? colors.slice() : undefined;
  return { color: (p) => new THREE.Color(colorIntAt(table, p)) };
}
